import React, { useEffect, useState } from 'react';
import styled, { type DefaultTheme } from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { useBridge } from '../../bridge/useBridge';

/**
 * landing-page (HY10 Membership) — a static, single-screen marketing page.
 *
 * No fields, no backend, no wizard: it introduces HY10 and routes the visitor
 * to one of three external destinations via plain `target="_blank"` anchors.
 * Bypasses `StepWizard` (LESSONS § "skip StepWizard on flows with independent
 * entry points") and renders real content on first paint, so it stays
 * `readiness: 'auto'` — `FlowLoader` drops the host overlay for us.
 *
 * See PRD `flows/landing-page/PRD.md` and the source page model
 * `flows/landing-page/page-models/LNDNG_PG002.json`.
 */

const FLOW_ID = 'landing-page';

// Outbound CTA targets + hero asset. Hardcoded HY10 constants: the sandbox
// serves the same production links as prod (verified against
// test.app.hy10.uk), so these don't vary per environment, and this flow is
// HY10-specific by nature. See PRD § 4 / O-1 (amended).
const ONBOARDING_URL = 'https://onboarding.hy10.uk/auth/login';
const WAITLIST_URL = 'https://membership.hy10.uk/contact';
const WEBSITE_URL = 'https://hy10.uk';
const HERO_IMAGE_URL = 'https://static.hy10.uk/assets/card.png';

// Derive the @2x/@3x retina srcset from the base PNG by filename convention
// (…/card.png → …/card@2x.png 2x, …/card@3x.png 3x), matching the source.
const heroSrcSet = (base: string): string | undefined => {
  const m = base.match(/^(.*)(\.[a-z0-9]+)$/i);
  return m ? `${m[1]}@2x${m[2]} 2x, ${m[1]}@3x${m[2]} 3x` : undefined;
};

// Theme token with a bespoke-source fallback (PRD § 5, O-3). HY10's injected
// theme supplies the marketing palette (black CTA, Urbanist) in production; the
// fallbacks preserve the source design under the lum dev theme / standalone.
const tok = (theme: DefaultTheme, name: string, fallback: string): string => {
  const v = theme.colors?.[name];
  return typeof v === 'string' ? v : fallback;
};

// Bespoke marketing values that don't map to tenant tokens — kept local.
const MOBILE = '697px'; // single-column / horizontal-scroll breakpoint.
const ICON_COLOR = '#AFAFAF';

type BulletKey = 'accounts' | 'payments' | 'spending' | 'circle' | 'lifestyle';

const BULLET_ICON_PATHS: Record<BulletKey, ReadonlyArray<{ d: string; evenodd?: boolean }>> = {
  accounts: [
    { d: "M21 10.5H20V9.5C20 9.23478 19.8946 8.98043 19.7071 8.79289C19.5196 8.60536 19.2652 8.5 19 8.5C18.7348 8.5 18.4804 8.60536 18.2929 8.79289C18.1054 8.98043 18 9.23478 18 9.5V10.5H17C16.7348 10.5 16.4804 10.6054 16.2929 10.7929C16.1054 10.9804 16 11.2348 16 11.5C16 11.7652 16.1054 12.0196 16.2929 12.2071C16.4804 12.3946 16.7348 12.5 17 12.5H18V13.5C18 13.7652 18.1054 14.0196 18.2929 14.2071C18.4804 14.3946 18.7348 14.5 19 14.5C19.2652 14.5 19.5196 14.3946 19.7071 14.2071C19.8946 14.0196 20 13.7652 20 13.5V12.5H21C21.2652 12.5 21.5196 12.3946 21.7071 12.2071C21.8946 12.0196 22 11.7652 22 11.5C22 11.2348 21.8946 10.9804 21.7071 10.7929C21.5196 10.6054 21.2652 10.5 21 10.5ZM13.3 12.22C13.8336 11.7581 14.2616 11.1869 14.5549 10.545C14.8482 9.90316 15 9.20571 15 8.5C15 7.17392 14.4732 5.90215 13.5355 4.96447C12.5979 4.02678 11.3261 3.5 10 3.5C8.67392 3.5 7.40215 4.02678 6.46447 4.96447C5.52678 5.90215 5 7.17392 5 8.5C4.99999 9.20571 5.1518 9.90316 5.44513 10.545C5.73845 11.1869 6.16642 11.7581 6.7 12.22C5.30014 12.8539 4.11247 13.8775 3.27898 15.1685C2.4455 16.4596 2.00147 17.9633 2 19.5C2 19.7652 2.10536 20.0196 2.29289 20.2071C2.48043 20.3946 2.73478 20.5 3 20.5C3.26522 20.5 3.51957 20.3946 3.70711 20.2071C3.89464 20.0196 4 19.7652 4 19.5C4 17.9087 4.63214 16.3826 5.75736 15.2574C6.88258 14.1321 8.4087 13.5 10 13.5C11.5913 13.5 13.1174 14.1321 14.2426 15.2574C15.3679 16.3826 16 17.9087 16 19.5C16 19.7652 16.1054 20.0196 16.2929 20.2071C16.4804 20.3946 16.7348 20.5 17 20.5C17.2652 20.5 17.5196 20.3946 17.7071 20.2071C17.8946 20.0196 18 19.7652 18 19.5C17.9985 17.9633 17.5545 16.4596 16.721 15.1685C15.8875 13.8775 14.6999 12.8539 13.3 12.22ZM10 11.5C9.40666 11.5 8.82664 11.3241 8.33329 10.9944C7.83994 10.6648 7.45542 10.1962 7.22836 9.64805C7.0013 9.09987 6.94189 8.49667 7.05764 7.91473C7.1734 7.33279 7.45912 6.79824 7.87868 6.37868C8.29824 5.95912 8.83279 5.6734 9.41473 5.55764C9.99667 5.44189 10.5999 5.5013 11.1481 5.72836C11.6962 5.95542 12.1648 6.33994 12.4944 6.83329C12.8241 7.32664 13 7.90666 13 8.5C13 9.29565 12.6839 10.0587 12.1213 10.6213C11.5587 11.1839 10.7956 11.5 10 11.5Z" },
  ],
  payments: [
    { d: "M16.968 3H17.032C17.4706 2.99999 17.8491 2.99998 18.1624 3.02135C18.4922 3.04386 18.8221 3.09336 19.1481 3.22836C19.8831 3.53285 20.4672 4.11687 20.7716 4.85195C20.9066 5.17788 20.9561 5.50779 20.9787 5.83762C21 6.15088 21 6.52936 21 6.96801V7.03199C21 7.47064 21 7.84913 20.9787 8.16238C20.9561 8.49221 20.9066 8.82212 20.7716 9.14805C20.4672 9.88314 19.8831 10.4672 19.1481 10.7716C18.8221 10.9066 18.4922 10.9561 18.1624 10.9787C17.8491 11 17.4706 11 17.032 11H16.968C16.5293 11 16.1509 11 15.8376 10.9787C15.5078 10.9561 15.1779 10.9066 14.852 10.7716C14.1169 10.4672 13.5328 9.88314 13.2284 9.14805C13.0934 8.82212 13.0439 8.49221 13.0214 8.16238C13 7.84914 13 7.47062 13 7.03198V6.96797C13 6.52934 13 6.15087 13.0214 5.83762C13.0439 5.50779 13.0934 5.17788 13.2284 4.85195C13.5328 4.11687 14.1169 3.53285 14.852 3.22836C15.1779 3.09336 15.5078 3.04386 15.8376 3.02135C16.1509 2.99998 16.5294 2.99999 16.968 3ZM15.9738 5.01671C15.7458 5.03227 15.6589 5.05889 15.6173 5.07612C15.3723 5.17762 15.1776 5.37229 15.0761 5.61732C15.0589 5.65893 15.0323 5.74576 15.0167 5.97376C15.0005 6.21074 15 6.52038 15 7C15 7.47963 15.0005 7.78926 15.0167 8.02624C15.0323 8.25424 15.0589 8.34107 15.0761 8.38269C15.1776 8.62771 15.3723 8.82239 15.6173 8.92388C15.6589 8.94112 15.7458 8.96774 15.9738 8.98329C16.2107 8.99946 16.5204 9 17 9C17.4796 9 17.7893 8.99946 18.0262 8.98329C18.2542 8.96774 18.3411 8.94112 18.3827 8.92388C18.6277 8.82239 18.8224 8.62771 18.9239 8.38269C18.9411 8.34107 18.9677 8.25424 18.9833 8.02624C18.9995 7.78926 19 7.47963 19 7C19 6.52038 18.9995 6.21074 18.9833 5.97376C18.9677 5.74576 18.9411 5.65893 18.9239 5.61732C18.8224 5.37229 18.6277 5.17762 18.3827 5.07612C18.3411 5.05889 18.2542 5.03227 18.0262 5.01671C17.7893 5.00054 17.4796 5 17 5C16.5204 5 16.2107 5.00054 15.9738 5.01671Z", evenodd: true },
    { d: "M7.03199 13H6.96802C6.52939 13 6.15087 13 5.83762 13.0214C5.50779 13.0439 5.17788 13.0934 4.85195 13.2284C4.11687 13.5328 3.53285 14.1169 3.22836 14.852C3.09336 15.1779 3.04386 15.5078 3.02135 15.8376C2.99998 16.1509 2.99999 16.5294 3 16.9679V17.032C2.99999 17.4707 2.99998 17.8491 3.02135 18.1624C3.04386 18.4922 3.09336 18.8221 3.22836 19.1481C3.53285 19.8831 4.11687 20.4672 4.85195 20.7716C5.17788 20.9066 5.50779 20.9561 5.83762 20.9787C6.15088 21 6.52936 21 6.96801 21H7.03199C7.47064 21 7.84913 21 8.16238 20.9787C8.49221 20.9561 8.82212 20.9066 9.14805 20.7716C9.88314 20.4672 10.4672 19.8831 10.7716 19.1481C10.9066 18.8221 10.9561 18.4922 10.9787 18.1624C11 17.8491 11 17.4706 11 17.032V16.968C11 16.5293 11 16.1509 10.9787 15.8376C10.9561 15.5078 10.9066 15.1779 10.7716 14.852C10.4672 14.1169 9.88314 13.5328 9.14805 13.2284C8.82212 13.0934 8.49221 13.0439 8.16238 13.0214C7.84914 13 7.47062 13 7.03199 13ZM5.61732 15.0761C5.65893 15.0589 5.74576 15.0323 5.97376 15.0167C6.21074 15.0005 6.52038 15 7 15C7.47963 15 7.78926 15.0005 8.02624 15.0167C8.25424 15.0323 8.34107 15.0589 8.38269 15.0761C8.62771 15.1776 8.82239 15.3723 8.92388 15.6173C8.94112 15.6589 8.96774 15.7458 8.98329 15.9738C8.99946 16.2107 9 16.5204 9 17C9 17.4796 8.99946 17.7893 8.98329 18.0262C8.96774 18.2542 8.94112 18.3411 8.92388 18.3827C8.82239 18.6277 8.62771 18.8224 8.38269 18.9239C8.34107 18.9411 8.25424 18.9677 8.02624 18.9833C7.78926 18.9995 7.47963 19 7 19C6.52038 19 6.21074 18.9995 5.97376 18.9833C5.74576 18.9677 5.65893 18.9411 5.61732 18.9239C5.37229 18.8224 5.17762 18.6277 5.07612 18.3827C5.05889 18.3411 5.03227 18.2542 5.01671 18.0262C5.00054 17.7893 5 17.4796 5 17C5 16.5204 5.00054 16.2107 5.01671 15.9738C5.03227 15.7458 5.05889 15.6589 5.07612 15.6173C5.17762 15.3723 5.37229 15.1776 5.61732 15.0761Z", evenodd: true },
    { d: "M17.032 13C17.4706 13 17.8491 13 18.1624 13.0214C18.4922 13.0439 18.8221 13.0934 19.1481 13.2284C19.8831 13.5328 20.4672 14.1169 20.7716 14.852C20.9066 15.1779 20.9561 15.5078 20.9787 15.8376C21 16.1509 21 16.5294 21 16.968V17.032C21 17.4706 21 17.8491 20.9787 18.1624C20.9561 18.4922 20.9066 18.8221 20.7716 19.1481C20.4672 19.8831 19.8831 20.4672 19.1481 20.7716C18.8221 20.9066 18.4922 20.9561 18.1624 20.9787C17.8491 21 17.4706 21 17.032 21H16.968C16.5294 21 16.1509 21 15.8376 20.9787C15.5078 20.9561 15.1779 20.9066 14.852 20.7716C14.1169 20.4672 13.5328 19.8831 13.2284 19.1481C13.0934 18.8221 13.0439 18.4922 13.0214 18.1624C13 17.8491 13 17.4707 13 17.032V16.968C13 16.5294 13 16.1509 13.0214 15.8376C13.0439 15.5078 13.0934 15.1779 13.2284 14.852C13.5328 14.1169 14.1169 13.5328 14.852 13.2284C15.1779 13.0934 15.5078 13.0439 15.8376 13.0214C16.1509 13 16.5294 13 16.968 13H17.032ZM15.9738 15.0167C15.7458 15.0323 15.6589 15.0589 15.6173 15.0761C15.3723 15.1776 15.1776 15.3723 15.0761 15.6173C15.0589 15.6589 15.0323 15.7458 15.0167 15.9738C15.0005 16.2107 15 16.5204 15 17C15 17.4796 15.0005 17.7893 15.0167 18.0262C15.0323 18.2542 15.0589 18.3411 15.0761 18.3827C15.1776 18.6277 15.3723 18.8224 15.6173 18.9239C15.6589 18.9411 15.7458 18.9677 15.9738 18.9833C16.2107 18.9995 16.5204 19 17 19C17.4796 19 17.7893 18.9995 18.0262 18.9833C18.2542 18.9677 18.3411 18.9411 18.3827 18.9239C18.6277 18.8224 18.8224 18.6277 18.9239 18.3827C18.9411 18.3411 18.9677 18.2542 18.9833 18.0262C18.9995 17.7893 19 17.4796 19 17C19 16.5204 18.9995 16.2107 18.9833 15.9738C18.9677 15.7458 18.9411 15.6589 18.9239 15.6173C18.8224 15.3723 18.6277 15.1776 18.3827 15.0761C18.3411 15.0589 18.2542 15.0323 18.0262 15.0167C17.7893 15.0005 17.4796 15 17 15C16.5204 15 16.2107 15.0005 15.9738 15.0167Z", evenodd: true },
    { d: "M7.03198 3H6.96802C6.52941 2.99999 6.15086 2.99998 5.83762 3.02135C5.50779 3.04386 5.17788 3.09336 4.85195 3.22836C4.11687 3.53284 3.53284 4.11687 3.22836 4.85195C3.09336 5.17788 3.04386 5.50779 3.02135 5.83762C2.99998 6.15086 2.99999 6.52932 3 6.96794V7.03198C2.99999 7.4706 2.99998 7.84915 3.02135 8.16238C3.04386 8.49221 3.09336 8.82212 3.22836 9.14805C3.53284 9.88314 4.11687 10.4672 4.85195 10.7716C5.17788 10.9066 5.50779 10.9561 5.83762 10.9787C6.15088 11 6.52936 11 6.96801 11H7.03199C7.47064 11 7.84913 11 8.16238 10.9787C8.49221 10.9561 8.82212 10.9066 9.14805 10.7716C9.88314 10.4672 10.4672 9.88314 10.7716 9.14805C10.9066 8.82212 10.9561 8.49221 10.9787 8.16238C11 7.84913 11 7.47064 11 7.03199V6.96801C11 6.52936 11 6.15088 10.9787 5.83762C10.9561 5.50779 10.9066 5.17788 10.7716 4.85195C10.4672 4.11687 9.88314 3.53284 9.14805 3.22836C8.82212 3.09336 8.49221 3.04386 8.16238 3.02135C7.84915 2.99998 7.4706 2.99999 7.03198 3ZM5.61732 5.07612C5.65893 5.05888 5.74576 5.03227 5.97376 5.01671C6.21074 5.00054 6.52038 5 7 5C7.47963 5 7.78926 5.00054 8.02624 5.01671C8.25424 5.03227 8.34107 5.05888 8.38268 5.07612C8.62771 5.17762 8.82239 5.37229 8.92388 5.61732C8.94112 5.65893 8.96773 5.74576 8.98329 5.97376C8.99946 6.21074 9 6.52038 9 7C9 7.47963 8.99946 7.78926 8.98329 8.02624C8.96773 8.25424 8.94112 8.34107 8.92388 8.38268C8.82239 8.62771 8.62771 8.82239 8.38268 8.92388C8.34107 8.94112 8.25424 8.96773 8.02624 8.98329C7.78926 8.99946 7.47963 9 7 9C6.52038 9 6.21074 8.99946 5.97376 8.98329C5.74576 8.96773 5.65893 8.94112 5.61732 8.92388C5.37229 8.82239 5.17762 8.62771 5.07612 8.38268C5.05888 8.34107 5.03227 8.25424 5.01671 8.02624C5.00054 7.78926 5 7.47963 5 7C5 6.52038 5.00054 6.21074 5.01671 5.97376C5.03227 5.74576 5.05888 5.65893 5.07612 5.61732C5.17762 5.37229 5.37229 5.17762 5.61732 5.07612Z", evenodd: true },
  ],
  spending: [
    { d: "M5 10.8478C3.83774 10.4595 3 9.36228 3 8.06941C3 6.82807 3.78259 5.72157 4.95303 5.30804L11.0006 3.17136C11.6473 2.94288 12.3527 2.94288 12.9994 3.17136L19.047 5.30804C20.2174 5.72157 21 6.82807 21 8.06941C21 9.36227 20.1623 10.4594 19 10.8477V15.1687C20.1652 15.5806 21 16.6918 21 17.998C21 19.6549 19.6569 20.998 18 20.998H6C4.34315 20.998 3 19.6549 3 17.998C3 16.6918 3.83481 15.5806 5 15.1687V10.8478ZM5.92207 8.99805H18.0779C18.5878 8.9945 19 8.5801 19 8.06941C19 7.67579 18.7518 7.32493 18.3807 7.1938L12.3331 5.05712C12.1176 4.98096 11.8824 4.98096 11.6669 5.05712L5.61929 7.1938C5.24815 7.32493 5 7.67579 5 8.06941C5 8.5801 5.41221 8.9945 5.92207 8.99805ZM7 10.998V14.998H9V10.998H7ZM11 10.998V14.998H13V10.998H11ZM15 10.998V14.998H17V10.998H15ZM18 16.998C18.5523 16.998 19 17.4457 19 17.998C19 18.5503 18.5523 18.998 18 18.998H6C5.44772 18.998 5 18.5503 5 17.998C5 17.4457 5.44772 16.998 6 16.998H18Z", evenodd: true },
  ],
  circle: [
    { d: "M21 12.0001H18V3.00007C18.0007 2.82386 17.9548 2.65059 17.867 2.49781C17.7792 2.34504 17.6526 2.21817 17.5 2.13007C17.348 2.0423 17.1755 1.99609 17 1.99609C16.8245 1.99609 16.652 2.0423 16.5 2.13007L13.5 3.85007L10.5 2.13007C10.348 2.0423 10.1755 1.99609 10 1.99609C9.82447 1.99609 9.65203 2.0423 9.50001 2.13007L6.50001 3.85007L3.50001 2.13007C3.34799 2.0423 3.17554 1.99609 3.00001 1.99609C2.82447 1.99609 2.65203 2.0423 2.50001 2.13007C2.3474 2.21817 2.22079 2.34504 2.13299 2.49781C2.04518 2.65059 1.99931 2.82386 2.00001 3.00007V19.0001C2.00001 19.7957 2.31608 20.5588 2.87869 21.1214C3.4413 21.684 4.20436 22.0001 5.00001 22.0001H19C19.7957 22.0001 20.5587 21.684 21.1213 21.1214C21.6839 20.5588 22 19.7957 22 19.0001V13.0001C22 12.7349 21.8947 12.4805 21.7071 12.293C21.5196 12.1054 21.2652 12.0001 21 12.0001ZM5.00001 20.0001C4.73479 20.0001 4.48044 19.8947 4.2929 19.7072C4.10536 19.5196 4.00001 19.2653 4.00001 19.0001V4.73007L6.00001 5.87007C6.15435 5.95068 6.32589 5.99278 6.50001 5.99278C6.67413 5.99278 6.84567 5.95068 7.00001 5.87007L10 4.15007L13 5.87007C13.1543 5.95068 13.3259 5.99278 13.5 5.99278C13.6741 5.99278 13.8457 5.95068 14 5.87007L16 4.73007V19.0001C16.0027 19.3412 16.0636 19.6794 16.18 20.0001H5.00001ZM20 19.0001C20 19.2653 19.8947 19.5196 19.7071 19.7072C19.5196 19.8947 19.2652 20.0001 19 20.0001C18.7348 20.0001 18.4804 19.8947 18.2929 19.7072C18.1054 19.5196 18 19.2653 18 19.0001V14.0001H20V19.0001Z" },
    { d: "M10.9984 13.59L11.7883 12.79C11.9767 12.6017 12.232 12.4959 12.4984 12.4959C12.7647 12.4959 13.02 12.6017 13.2083 12.79C13.3967 12.9783 13.5024 13.2337 13.5024 13.5C13.5024 13.7663 13.3967 14.0217 13.2083 14.21L10.7083 16.71C10.6132 16.801 10.5011 16.8724 10.3783 16.92C10.1349 17.02 9.86181 17.02 9.61835 16.92C9.4956 16.8724 9.38345 16.801 9.28835 16.71L6.78835 14.21C6.69462 14.117 6.62023 14.0064 6.56946 13.8846C6.51869 13.7627 6.49255 13.632 6.49255 13.5C6.49255 13.368 6.51869 13.2373 6.56946 13.1154C6.62023 12.9936 6.69462 12.883 6.78835 12.79C6.88131 12.6963 6.99191 12.6219 7.11377 12.5711C7.23563 12.5203 7.36634 12.4942 7.49835 12.4942C7.63036 12.4942 7.76107 12.5203 7.88293 12.5711C8.00479 12.6219 8.11539 12.6963 8.20835 12.79L8.99835 13.59L8.99835 9C8.99835 8.73478 9.10371 8.48043 9.29124 8.29289C9.47878 8.10536 9.73313 8 9.99835 8C10.2636 8 10.5179 8.10536 10.7055 8.29289C10.893 8.48043 10.9984 8.73478 10.9984 9V13.59Z" },
  ],
  lifestyle: [
    { d: "M22 17H2C1.73478 17 1.48043 17.1054 1.29289 17.2929C1.10536 17.4804 1 17.7348 1 18C1 18.2652 1.10536 18.5196 1.29289 18.7071C1.48043 18.8946 1.73478 19 2 19H22C22.2652 19 22.5196 18.8946 22.7071 18.7071C22.8946 18.5196 23 18.2652 23 18C23 17.7348 22.8946 17.4804 22.7071 17.2929C22.5196 17.1054 22.2652 17 22 17ZM22 21H2C1.73478 21 1.48043 21.1054 1.29289 21.2929C1.10536 21.4804 1 21.7348 1 22C1 22.2652 1.10536 22.5196 1.29289 22.7071C1.48043 22.8946 1.73478 23 2 23H22C22.2652 23 22.5196 22.8946 22.7071 22.7071C22.8946 22.5196 23 22.2652 23 22C23 21.7348 22.8946 21.4804 22.7071 21.2929C22.5196 21.1054 22.2652 21 22 21ZM6 7C5.80222 7 5.60888 7.05865 5.44443 7.16853C5.27998 7.27841 5.15181 7.43459 5.07612 7.61732C5.00043 7.80004 4.98063 8.00111 5.01921 8.19509C5.0578 8.38907 5.15304 8.56725 5.29289 8.70711C5.43275 8.84696 5.61093 8.9422 5.80491 8.98079C5.99889 9.01937 6.19996 8.99957 6.38268 8.92388C6.56541 8.84819 6.72159 8.72002 6.83147 8.55557C6.94135 8.39112 7 8.19778 7 8C7 7.73478 6.89464 7.48043 6.70711 7.29289C6.51957 7.10536 6.26522 7 6 7ZM20 1H4C3.20435 1 2.44129 1.31607 1.87868 1.87868C1.31607 2.44129 1 3.20435 1 4V12C1 12.7956 1.31607 13.5587 1.87868 14.1213C2.44129 14.6839 3.20435 15 4 15H20C20.7956 15 21.5587 14.6839 22.1213 14.1213C22.6839 13.5587 23 12.7956 23 12V4C23 3.20435 22.6839 2.44129 22.1213 1.87868C21.5587 1.31607 20.7956 1 20 1ZM21 12C21 12.2652 20.8946 12.5196 20.7071 12.7071C20.5196 12.8946 20.2652 13 20 13H4C3.73478 13 3.48043 12.8946 3.29289 12.7071C3.10536 12.5196 3 12.2652 3 12V4C3 3.73478 3.10536 3.48043 3.29289 3.29289C3.48043 3.10536 3.73478 3 4 3H20C20.2652 3 20.5196 3.10536 20.7071 3.29289C20.8946 3.48043 21 3.73478 21 4V12ZM12 5C11.4067 5 10.8266 5.17595 10.3333 5.50559C9.83994 5.83524 9.45542 6.30377 9.22836 6.85195C9.0013 7.40013 8.94189 8.00333 9.05764 8.58527C9.1734 9.16721 9.45912 9.70176 9.87868 10.1213C10.2982 10.5409 10.8328 10.8266 11.4147 10.9424C11.9967 11.0581 12.5999 10.9987 13.1481 10.7716C13.6962 10.5446 14.1648 10.1601 14.4944 9.66671C14.8241 9.17336 15 8.59334 15 8C15 7.20435 14.6839 6.44129 14.1213 5.87868C13.5587 5.31607 12.7956 5 12 5ZM12 9C11.8022 9 11.6089 8.94135 11.4444 8.83147C11.28 8.72159 11.1518 8.56541 11.0761 8.38268C11.0004 8.19996 10.9806 7.99889 11.0192 7.80491C11.0578 7.61093 11.153 7.43275 11.2929 7.29289C11.4327 7.15304 11.6109 7.0578 11.8049 7.01921C11.9989 6.98063 12.2 7.00043 12.3827 7.07612C12.5654 7.15181 12.7216 7.27998 12.8315 7.44443C12.9414 7.60888 13 7.80222 13 8C13 8.26522 12.8946 8.51957 12.7071 8.70711C12.5196 8.89464 12.2652 9 12 9ZM18 7C17.8022 7 17.6089 7.05865 17.4444 7.16853C17.28 7.27841 17.1518 7.43459 17.0761 7.61732C17.0004 7.80004 16.9806 8.00111 17.0192 8.19509C17.0578 8.38907 17.153 8.56725 17.2929 8.70711C17.4327 8.84696 17.6109 8.9422 17.8049 8.98079C17.9989 9.01937 18.2 8.99957 18.3827 8.92388C18.5654 8.84819 18.7216 8.72002 18.8315 8.55557C18.9414 8.39112 19 8.19778 19 8C19 7.73478 18.8946 7.48043 18.7071 7.29289C18.5196 7.10536 18.2652 7 18 7Z" },
  ],
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 0 16px;
  background-color: ${({ theme }) => tok(theme, 'general_background', '#F2F3F3')};
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
  font-family: ${({ theme }) => tok(theme, 'font_family', 'Urbanist')}, sans-serif;
  @media (max-width: ${MOBILE}) {
    padding: 0;
  }
`;

const Landing = styled.div`
  width: 100%;
  max-width: 640px;
  margin-bottom: 24px;
  /* Mobile inset is padding, not margin: with width:100% + border-box a side
     margin would add 32px outside the width and overflow the viewport (clips
     the title on the left). Padding stays inside the width. The full-bleed
     hero/bullets below cancel this 16px with negative margins. */
  @media (max-width: ${MOBILE}) {
    padding: 0 16px;
  }
`;

const CardImageContainer = styled.div`
  position: relative;
  margin: 16px 0 29px;
  @media (max-width: ${MOBILE}) {
    margin-bottom: 22px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: auto;
  user-select: none;
  pointer-events: none;
  border-radius: 28px;
  aspect-ratio: 16 / 9;
  background-color: #e9e9e9;
  @media (max-width: ${MOBILE}) {
    width: calc(100% + 32px);
    border-radius: 16px;
    margin-left: -16px;
    margin-right: -16px;
  }
`;

const CardImageLabel = styled.span`
  position: absolute;
  display: block;
  width: 100%;
  bottom: 14px;
  text-align: center;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

// Text reproduces the bespoke marketing type scale from the source CSS
// (LNDNG_PG002) verbatim — colour + font-family are inherited from Root (theme
// tokens). The DS Heading/Body atoms were intentionally NOT used here: they
// encode the tenant type scale (sizes, line-heights, own margins), which
// doesn't match this page and broke the vertical rhythm. See PRD § 5.
const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 30px;
  font-weight: 700;
  line-height: 28px;
  @media (max-width: ${MOBILE}) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const InviteTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  @media (max-width: ${MOBILE}) {
    font-size: 16px;
    margin-bottom: 0;
  }
`;

const InviteDescription = styled.p`
  margin: 0 0 32px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  @media (max-width: ${MOBILE}) {
    margin-bottom: 16px;
  }
`;

const BulletTitle = styled.p`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
`;

const BulletDescription = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const Bullets = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  @media (max-width: ${MOBILE}) {
    flex-direction: column;
    overflow-x: auto;
    height: 135px;
    margin: 0 -16px 2px;
    padding: 0 16px;
  }
`;

const Bullet = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: calc(50% - 6px);
  height: 113px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => tok(theme, 'containers_bordered_border', '#D1D1D1')};
  box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.04), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);
  @media (max-width: ${MOBILE}) {
    width: 295px;
  }
`;

const BulletSvg = styled.svg`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${ICON_COLOR};
`;

const BulletContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Divider = styled.hr`
  width: 100%;
  border: 0;
  margin: 32px 0 41px;
  border-top: 1px solid ${({ theme }) => tok(theme, 'general_dividers', '#DEE2E4')};
  @media (max-width: ${MOBILE}) {
    margin: 24px -16px;
  }
`;

const ctaBase = `
  display: flex;
  flex: 1 0 0;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-decoration: none;
  &:hover,
  &:active {
    text-decoration: none;
  }
`;

const PrimaryCta = styled.a`
  ${ctaBase}
  color: ${({ theme }) => tok(theme, 'standard_button_primary_default_font', '#FFFFFF')};
  background: ${({ theme }) => tok(theme, 'standard_button_primary_default_background', '#131313')};
`;

const SecondaryCta = styled.a`
  ${ctaBase}
  margin-bottom: 24px;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
  border: 2px solid ${({ theme }) => tok(theme, 'containers_bordered_border', '#D1D1D1')};
  &:hover {
    background-color: rgb(237, 239, 240);
    color: rgb(96, 100, 103);
  }
`;

const LinkCta = styled.a`
  ${ctaBase}
  flex-grow: 0;
  padding: 0;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const BulletIcon: React.FC<{ name: BulletKey }> = ({ name }) => (
  <BulletSvg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    {BULLET_ICON_PATHS[name].map((p, i) => (
      <path
        key={i}
        d={p.d}
        fill="currentColor"
        {...(p.evenodd ? { fillRule: 'evenodd', clipRule: 'evenodd' } : {})}
      />
    ))}
  </BulletSvg>
);

export const LandingPageFlow: React.FC = () => {
  const bridge = useBridge();
  const [heroFailed, setHeroFailed] = useState(false);

  // Bypassing StepWizard: emit step:change + the viewed breadcrumb once on mount.
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'landing',
      stepIndex: 0,
      totalSteps: 1,
    });
    bridge.emit({ type: 'telemetry', event: 'landing-page.viewed' });
    // Once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal → ask the host to close/leave the embed.
  useEffect(
    () => bridge.onBackRequest(() => bridge.emit({ type: 'cancel', flowId: FLOW_ID })),
    [bridge],
  );

  const trackCta = (event: string) => bridge.emit({ type: 'telemetry', event });

  return (
    <Root>
      <Landing>
        <CardImageContainer>
          {!heroFailed && (
            <CardImage
              src={HERO_IMAGE_URL}
              srcSet={heroSrcSet(HERO_IMAGE_URL)}
              alt={t`HY10 card`}
              onError={() => setHeroFailed(true)}
            />
          )}
          <CardImageLabel>
            <Trans>Unlimited. Understood.</Trans>
          </CardImageLabel>
        </CardImageContainer>

        <Section>
          <Title>
            <Trans>Join an Unlimited world</Trans>
          </Title>
          <Description>
            <Trans>
              Made for ultra high-net worth individuals, HY10 gives you complete control of your
              money – across accounts, currencies, cards, people, and lifestyles.
            </Trans>
          </Description>

          <Bullets>
            <Bullet>
              <BulletIcon name="accounts" />
              <BulletContent>
                <BulletTitle>
                  <Trans>Multi-currency accounts (IBAN)</Trans>
                </BulletTitle>
                <BulletDescription>
                  <Trans>Open unlimited multi-currency accounts built around your needs</Trans>
                </BulletDescription>
              </BulletContent>
            </Bullet>
            <Bullet>
              <BulletIcon name="payments" />
              <BulletContent>
                <BulletTitle>
                  <Trans>Global payments</Trans>
                </BulletTitle>
                <BulletDescription>
                  <Trans>Send money globally with high-value cross-border transfers</Trans>
                </BulletDescription>
              </BulletContent>
            </Bullet>
            <Bullet>
              <BulletIcon name="spending" />
              <BulletContent>
                <BulletTitle>
                  <Trans>Unlimited spending power</Trans>
                </BulletTitle>
                <BulletDescription>
                  <Trans>Adaptive spending thresholds fit how you spend and live your life</Trans>
                </BulletDescription>
              </BulletContent>
            </Bullet>
            <Bullet>
              <BulletIcon name="circle" />
              <BulletContent>
                <BulletTitle>
                  <Trans>Create your inner circle</Trans>
                </BulletTitle>
                <BulletDescription>
                  <Trans>
                    Grant advisors or personal assistants access to manage your daily finances
                  </Trans>
                </BulletDescription>
              </BulletContent>
            </Bullet>
            <Bullet>
              <BulletIcon name="lifestyle" />
              <BulletContent>
                <BulletTitle>
                  <Trans>24/7 lifestyle manager</Trans>
                </BulletTitle>
                <BulletDescription>
                  <Trans>Your dedicated lifestyle manager is available any time, anywhere.</Trans>
                </BulletDescription>
              </BulletContent>
            </Bullet>
          </Bullets>

          <PrimaryCta
            href={ONBOARDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta('landing-page.cta.start-onboarding')}
          >
            <Trans>Start Onboarding</Trans>
          </PrimaryCta>
        </Section>

        <Divider />

        <Section>
          <InviteTitle>
            <Trans>HY10 is invite-only</Trans>
          </InviteTitle>
          <InviteDescription>
            <Trans>
              If you haven’t received an invitation, we encourage you to join our waitlist.
            </Trans>
          </InviteDescription>
          <SecondaryCta
            href={WAITLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta('landing-page.cta.join-waitlist')}
          >
            <Trans>Join the waitlist</Trans>
          </SecondaryCta>
          <LinkCta
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta('landing-page.cta.find-out-more')}
          >
            <Trans>Find out more at hy10.uk</Trans>
          </LinkCta>
        </Section>
      </Landing>
    </Root>
  );
};

export default LandingPageFlow;
